/*
Month calendars for the side column of the /calendar/events/ page: this month and the next, weeks
from Monday. A day with events is a link to its group on the page, marked with a dot;
today is marked.
*/
import {
    dateKey,
    dayAnchor,
    element,
    groupByDay,
    hasExactDay,
    isUnconfirmedPassed,
} from './eventsData';

const MONTHS_SHOWN = 2;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * @param {Array<Object>} events all events from the calendar
 * @param {Date} now
 * @returns {Array<HTMLElement>} one element per month
 */
export function buildMonths(events, now) {
    const byDay = groupByDay(events.filter((e) => hasExactDay(e) && !isUnconfirmedPassed(e)));
    const todayKey = dateKey(now);
    return Array.from({ length: MONTHS_SHOWN }, (_, offset) => {
        const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
        return buildMonth(first, byDay, todayKey);
    });
}

function buildMonth(first, byDay, todayKey) {
    const month = element('section', 'th-events-month');
    const title = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const weekdays = element('p', 'th-events-month__weekdays');
    weekdays.setAttribute('aria-hidden', 'true');
    WEEKDAYS.forEach((name) => weekdays.append(element('span', '', name)));
    const days = element('ol', 'th-events-month__days');
    const leadingBlanks = (first.getDay() + 6) % WEEKDAYS.length;
    for (let blank = 0; blank < leadingBlanks; blank += 1) {
        days.append(element('li', 'th-events-month__blank'));
    }
    const length = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    for (let number = 1; number <= length; number += 1) {
        const day = new Date(first.getFullYear(), first.getMonth(), number);
        days.append(buildDay(day, byDay, todayKey));
    }
    month.append(element('h3', 'th-events-month__title', title), weekdays, days);
    return month;
}

function buildDay(day, byDay, todayKey) {
    const key = dateKey(day);
    const events = byDay.get(key);
    const cell = element('li', 'th-events-month__day');
    cell.dataset.today = String(key === todayKey);
    if (!events) {
        cell.textContent = String(day.getDate());
        return cell;
    }
    // Past days have no group on the page (past events sit in a closed list): dot, no link.
    const mark = element(key < todayKey ? 'span' : 'a', 'th-events-month__mark', String(day.getDate()));
    if (key >= todayKey) {
        mark.classList.add('th-events-month__mark--link');
        mark.href = `#${dayAnchor(key)}`;
        const label = day.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
        mark.setAttribute('aria-label', `${label}: ${events.length} event${events.length > 1 ? 's' : ''}`);
    }
    cell.append(mark);
    return cell;
}
